import { Box, Button, Grid, Paper, Stack, TableContainer, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { DateRangePicker } from '~/components/Form/DatePicker';
import { AmountRangePicker } from '~/components/Form/RangePicker';
import { Search } from '~/components/Search';
import { TablePagination } from '~/components/Table';
import { usePagination } from '~/hooks/usePagination';
import { useTableSorting } from '~/hooks/useTableSorting';
import { useExpenses } from '~/models/Expense/hooks';
import { ROUTES } from '~/router/Routes';
import { AmplitudeEvent } from '~/types/amplitude';
import { DateRange } from '~/types/date';
import { Expense } from '~/types/expense';
import { AmountRange } from '~/types/range';
import { SortDirection } from '~/types/table';
import { sendAmplitudeData } from '~/utils/amplitude';

import { ExpensesTable } from './ExpensesTable';

enum SearchParam {
  search = 'search',
  dateRange = 'dateRange',
  amountRange = 'amountRange',
}

export const ExpensesList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const [search, setSearch] = useState(searchParams.get(SearchParam.search) || '');
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const initialDateRange = searchParams.get(SearchParam.dateRange);

    if (initialDateRange) {
      return JSON.parse(initialDateRange);
    }
    return [undefined, undefined];
  });
  const [amountRange, setAmountRange] = useState<AmountRange>(() => {
    const initialAmountRange = searchParams.get(SearchParam.amountRange);

    if (initialAmountRange) {
      return JSON.parse(initialAmountRange);
    }
    return {};
  });
  const [count, setCount] = useState(0);

  const { ordering, currentSort, sortDirection, onSort } = useTableSorting<Expense>(
    'id',
    SortDirection.Desc,
  );

  const { offset, page, rowsPerPage, onPageChange, onRowsPerPageChange } = usePagination(count);

  const getDateFrom = () => (!dateRange[0] && dateRange[1] ? dateRange[1] : dateRange[0]);

  const getDateTo = () => (!dateRange[1] && dateRange[0] ? dateRange[0] : dateRange[1]);

  const { data, isLoading } = useExpenses({
    offset,
    limit: rowsPerPage,
    ordering,
    search,
    issue_date_from: getDateFrom(),
    issue_date_to: getDateTo(),
    full_amount_min: amountRange.min,
    full_amount_max: amountRange.max,
  });

  const onSearch = (searchTerm: string) => {
    if (searchTerm === search) return;

    searchParams.set(SearchParam.search, searchTerm);
    setSearchParams(searchParams);

    setSearch(searchTerm);
    onPageChange(1);
  };

  const onChangeDateRange = (newDateRange: DateRange) => {
    searchParams.set(SearchParam.dateRange, JSON.stringify(newDateRange));
    setSearchParams(searchParams);

    setDateRange(newDateRange);
    onPageChange(1);
  };

  useEffect(() => {
    if (data) {
      setCount(data.count);
    }
  }, [data]);

  const handleAmountRangeChange = (newAmountRange: AmountRange) => {
    searchParams.set(SearchParam.amountRange, JSON.stringify(newAmountRange));
    setSearchParams(searchParams);
    setAmountRange(newAmountRange);
  };

  useEffect(() => {
    sendAmplitudeData(AmplitudeEvent.ExpensesListRefreshed);
  }, []);

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={1}>
        <Typography variant="h5">{t('msg_expense_screen_title')}</Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          component={Link}
          to={ROUTES.DASHBOARD_EXPENSES_NEW}
        >
          {t('btn_add_new_expence')}
        </Button>
      </Stack>

      <Box>
        <Grid container spacing={2}>
          <Grid item sm={12} md={3}>
            <DateRangePicker
              placeholder="Date"
              variant="filled"
              onChange={onChangeDateRange}
              hasClearButton
              fullWidth
              defaultValue={dateRange}
            />
          </Grid>
          <Grid item sm={12} md={3}>
            <AmountRangePicker value={amountRange} onChange={handleAmountRangeChange} />
          </Grid>
          <Grid item sm={12} md={6}>
            <Search defaultValue={search} onSearch={onSearch} />
          </Grid>
        </Grid>
      </Box>

      <Paper>
        <TableContainer sx={{ position: 'relative' }}>
          <ExpensesTable
            items={data?.items}
            count={count}
            onSort={onSort}
            currentSort={currentSort}
            sortDirection={sortDirection}
            isLoading={isLoading}
          />
          <TablePagination
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        </TableContainer>
      </Paper>
    </Stack>
  );
};
